import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';

// services
import { TagsService } from '@app/tags/services/tags.service';
import { OnboardingService } from '@app/onboarding/services/onboarding.service';

// api
import { TagsApi } from '@app/tags/tag-api.model';
import { SkillItem } from '@app/onboarding/onboarding.model';
import { FeaturedCategories } from '@app/onboarding/onboarding-api.model';

// other
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

/**
 * Hmm tag picker Component
 * Is only used for HMM sparks
 * It creates dropdown sections of skills based on the Category
 *
 * @param tagSelected {EventEmitter<Skill>} Output of a toggled
 *                      skill used to update the `next`footer button
 * @example
 * ```
 * <dgx-hmm-tag-picker
 *      (tagSelected)="onTagSelected($event)"
 * ></dgx-hmm-tag-picker>
 * ```
 *
 */
@Component({
  selector: 'dgx-hmm-tag-picker',
  templateUrl: './hmm-tag-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HmmTagPickerComponent implements OnInit {
  @Output()
  public tagSelected: EventEmitter<boolean> = new EventEmitter<boolean>();
  public categories: Observable<FeaturedCategories[]>;
  public isLoading: boolean = true;

  constructor(
    private tagsService: TagsService,
    private onboardingService: OnboardingService,
    private cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.categories = this.onboardingService.getFeaturedCategories().pipe(
      tap((categories) => {
        this.tagSelected.emit(this.isAnySelected(categories));
        this.isLoading = false;
      })
    );
  }

  /**
   * On clicking a skill tile either add or remove the skill for the user
   * Emits a skill selected change
   * @param tag skill to be added/removed
   */
  public onToggleTag(tag: SkillItem, categories: FeaturedCategories[]): void {
    if (!tag.selected) {
      tag.selected = !tag.selected;
      this.tagsService
        .addUserTag(tag as unknown as TagsApi.Tag, 'Hmm-Skills')
        .subscribe((result: TagsApi.Tag) => {
          // Save the returned TagId so we can remove this tag later
          tag.tagId = result?.tagId;
          this.updateCategories(tag, categories);
        });
    } else if (tag.tagId) {
      tag.title = tag.name;
      this.tagsService
        .removeUserTag(tag as unknown as TagsApi.TagDetails, 'Hmm-Skills')
        .subscribe(() => {
          tag.selected = !tag.selected;
          this.updateCategories(tag, categories);
        });
    }
  }

  /**
   * Check through the categories if any of the skills are selected
   *
   * @param categories
   */
  private isAnySelected(categories: FeaturedCategories[]): boolean {
    return !!categories.find((category) => {
      return category.detailedSkills.find((tag) => tag.selected);
    });
  }

  /**
   * Given the list of categories update the detailed skill items
   * This will update each section of skills on the page.
   *
   * @param Skill that has been changed
   * @param categories list of categories containing the detailed skills
   */
  private updateCategories(
    skill: SkillItem,
    categories: FeaturedCategories[]
  ): void {
    for (const category of categories) {
      for (const skillItem of category.detailedSkills) {
        if (skill?.name === skillItem.name && skill?.tagId !== undefined) {
          skillItem.selected = skill?.selected;
          skillItem.tagId = skill?.tagId;
        }
      }
    }
    this.tagSelected.emit(this.isAnySelected(categories));
    this.cdr.detectChanges();
  }
}
